import React, { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CardShell } from './card-shell';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import type { ToolCardProps } from './types';
import { safeGet, safeArray } from './types';
import { useCvExportEntitlement } from '../../../hooks/useCvExportEntitlement';
import { downloadCvExport, type ExportFormat } from '../../../lib/cv-export';
import { downloadTailoredExport } from '../../../lib/tailored-cv-api';
import { trackProductEvent } from '../../../lib/product-analytics';

export const CVExportCard: FC<ToolCardProps> = ({ result }) => {
  const { t } = useTranslation('seeker');
  const cvExportAccess = useCvExportEntitlement();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState<Set<string>>(new Set());

  const cvId = safeGet<string>(result, 'cvId');
  const cvTitle = safeGet<string>(result, 'cvTitle') ?? 'My CV';
  const isTailored = safeGet<boolean>(result, 'isTailored') ?? false;
  const formats = safeArray<{ format: string; label: string }>(result, 'formats');

  if (!result || typeof result !== 'object' || formats.length === 0) return null;

  const handleDownload = async (format: string) => {
    if (downloading) return;
    if (cvExportAccess.isFormatLoading(format)) return;
    if (cvExportAccess.isFormatLocked(format)) {
      cvExportAccess.openUpgrade();
      return;
    }

    setDownloading(format);
    try {
      if (isTailored && cvId) {
        await downloadTailoredExport(cvId, format as ExportFormat);
      } else {
        await downloadCvExport(format as ExportFormat);
      }
      setDownloaded((prev) => new Set(prev).add(format));
    } catch (error) {
      // Download error handled by platform (browser/native); the failure is
      // still worth counting.
      trackProductEvent('cv_export_failed', {
        format,
        component: 'chat_export_card',
        tailored: isTailored,
        reason: error instanceof Error ? error.message.slice(0, 120) : 'unknown',
      });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MaterialSymbol
            name="download"
            size={16}
            style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
          />
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 500 }}>
            Export CV
          </span>
        </div>

        {/* Title */}
        <div
          style={{
            fontSize: 16,
            fontWeight: 500,
            color: 'var(--text-primary)',
            lineHeight: '22px',
          }}
        >
          {cvTitle}
        </div>

        {/* Tailored badge */}
        {isTailored && (
          <div>
            <span className="tabbio-menu-badge">Tailored</span>
          </div>
        )}

        {/* Download buttons */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {formats.map((f) => {
            const format = safeGet<string>(f, 'format') ?? '';
            const label = safeGet<string>(f, 'label') ?? format.toUpperCase();
            const isLocked = cvExportAccess.isFormatLocked(format);
            const isLoadingEntitlement = cvExportAccess.isFormatLoading(format);
            const isActive = downloading === format;
            const isDone = downloaded.has(format);

            return (
              <button
                type="button"
                key={format}
                onClick={() => handleDownload(format)}
                disabled={!!downloading || isLoadingEntitlement}
                className="tabbio-chip"
                style={{
                  cursor:
                    downloading || isLoadingEntitlement ? 'default' : 'pointer',
                  opacity:
                    (downloading && !isActive) || isLoadingEntitlement ? 0.5 : 1,
                }}
              >
                {isActive ? (
                  <MaterialSymbol
                    name="progress_activity"
                    size={16}
                    style={{ animation: 'tabbio-spinner-spin 1s linear infinite' }}
                  />
                ) : isLocked ? (
                  <MaterialSymbol name="lock" size={16} />
                ) : isDone ? (
                  <MaterialSymbol name="check" size={16} />
                ) : (
                  <MaterialSymbol name="description" size={16} />
                )}
                {label}
                {isLocked ? (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'var(--text-tertiary)',
                    }}
                  >
                    {t('cv.share.plusRequired')}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

    </CardShell>
  );
};
